import { Link, NavLink, Outlet } from 'react-router';
import { useLogout } from '../api/hooks.js';
import { initials } from '../lib/format.js';
import { useSessionStore } from '../store/session.js';
import { cx } from './classes.js';
import { BellIcon } from './Icons.js';
import { Mark } from './Mark.js';

const NAV = [
  { to: '/', label: 'Search', end: true },
  { to: '/watchlist', label: 'Watchlist', end: false },
  { to: '/alerts', label: 'Alerts', end: false },
  { to: '/records', label: 'Records', end: false },
] as const;

const ROLE_WORD = { admin: 'Admin', verifier: 'Verifier', auditor: 'Auditor' } as const;

/**
 * The console frame (every artboard's header): mark, the four nav words,
 * the alerts bell, and who is signed in — which is also the way to Settings.
 */
export function AppShell() {
  const memberEmail = useSessionStore((s) => s.memberEmail);
  const accountName = useSessionStore((s) => s.accountName);
  const role = useSessionStore((s) => s.role);
  const logout = useLogout();
  return (
    <div className="flex min-h-screen flex-col bg-ground">
      <header className="flex h-16 items-center justify-between border-b border-line bg-surface px-12">
        <div className="flex items-center gap-10">
          <Link
            to="/"
            className="flex items-center gap-2.5 text-[18px] font-semibold tracking-[-0.01em] text-ink"
          >
            <Mark />
            Dira
          </Link>
          <nav className="flex gap-7" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cx(
                    'border-b-2 px-0.5 py-1.5 text-[14px]',
                    isActive
                      ? 'border-accent font-semibold text-ink'
                      : 'border-transparent font-medium text-mute hover:text-ink',
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-5">
          <Link
            to="/alerts"
            aria-label="Alerts"
            className="relative flex size-9 items-center justify-center rounded-btn border border-line bg-surface text-ink hover:bg-sunk"
          >
            <BellIcon />
          </Link>
          <Link
            to="/settings"
            className="flex items-center gap-2.5 rounded-btn py-1 pr-1 hover:bg-sunk"
            aria-label="Settings"
          >
            <span className="flex size-8 items-center justify-center rounded-full bg-ink text-[12px] font-semibold text-surface">
              {memberEmail ? initials(memberEmail) : '·'}
            </span>
            <span className="flex flex-col leading-[1.2]">
              <span className="text-[13px] font-semibold">{memberEmail ?? ''}</span>
              <span className="text-[12px] text-mute">
                {accountName ?? ''}
                {role ? ` · ${ROLE_WORD[role]}` : ''}
              </span>
            </span>
          </Link>
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="cursor-pointer text-[13px] font-medium text-mute hover:text-ink"
          >
            Sign out
          </button>
        </div>
      </header>
      <Outlet />
    </div>
  );
}
